import React from 'react';
import Layout from '../../../components/Layout';
import Page from '../../../components/Page';
import fetch from '../../../core/fetch';

/**
 * Terms of Service Page Route Handler
 * 
 * Uses the new DynamicContent system via GraphQL for i18n.
 * Falls back to StaticPage if DynamicContent has no data.
 */

// Helper to normalize locale to short code
const getShortLocale = (locale) => {
  if (!locale) return 'es';
  return locale.substring(0, 2).toLowerCase();
};

// Helper to get localized field (fallback for StaticPage format)
const getLocalizedField = (data, field, locale) => {
  if (!data) return '';
  const code = getShortLocale(locale);
  if (code === 'es') return data[field] || '';
  const localizedField = `${field}_${code}`;
  return data[localizedField] || data[field] || '';
};

// Query for DynamicContent (new system)
const dynamicContentQuery = `query getDynamicContent($contentType: String!, $contentKey: String!, $locale: String!) {
  getDynamicContent(contentType: $contentType, contentKey: $contentKey, locale: $locale) {
    id
    content
    metaTitle
    metaDescription
    locale
  }
}`;

// Fallback query for StaticPage (legacy system)
const staticPageQuery = `query getEditStaticPage($id: Int!) {
  getEditStaticPage(id: $id) {
    id
    pageName
    content
    content_en
    content_fr
    content_it
    content_pt
    metaTitle
    metaTitle_en
    metaTitle_fr
    metaTitle_it
    metaTitle_pt
    metaDescription
    metaDescription_en
    metaDescription_fr
    metaDescription_it
    metaDescription_pt
  }
}`;

export default async function action({ locale }) {
  try {
    // First try the new DynamicContent system
    const dynamicResp = await fetch('/graphql', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: dynamicContentQuery,
        variables: {
          contentType: 'static_page',
          contentKey: 'terms-of-service',
          locale: locale || 'es'
        },
      }),
      credentials: 'include',
    });

    const dynamicData = await dynamicResp.json();
    const dynamicContent = dynamicData?.data?.getDynamicContent;

    // If DynamicContent has data, use it
    if (dynamicContent && dynamicContent.content) {
      return {
        title: dynamicContent.metaTitle || 'Terms of Service',
        description: dynamicContent.metaDescription || '',
        chunk: 'terms-of-service',
        component: <Layout><Page html={dynamicContent.content} title={dynamicContent.metaTitle} /></Layout>,
      };
    }

    // Fallback to StaticPage (legacy system)
    const staticResp = await fetch('/graphql', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: staticPageQuery,
        variables: { id: 7 },
      }),
      credentials: 'include',
    });

    const staticData = await staticResp.json();
    const page = staticData?.data?.getEditStaticPage;

    if (!page) {
      return undefined;
    }

    // Get localized content from StaticPage
    const title = getLocalizedField(page, 'metaTitle', locale);
    const description = getLocalizedField(page, 'metaDescription', locale);
    const content = getLocalizedField(page, 'content', locale);

    return {
      title: title || 'Terms of Service',
      description,
      chunk: 'terms-of-service',
      component: <Layout><Page html={content} title={title} /></Layout>,
    };
  } catch (error) {
    console.error('[Terms Page] Error loading content:', error);

    return {
      title: 'Terms of Service',
      description: '',
      chunk: 'terms-of-service',
      component: <Layout><Page html="<p>Error loading content</p>" title="Terms of Service" /></Layout>,
    };
  }
}
